import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Table } from '@/common';
import { Spinner } from '@/components/generic';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { PAGE_SIZE } from '@/constants';
import { PERMISSION_TYPE, PRIVATE_PAGE_ROUTES } from '@/enums';
import { getPaymentPlanColumns } from '@/helpers/PaymentPlansHelper';
import {
  useDeletePaymentPlanMutation,
  useGetAllPaymentPlans
} from '@/services/paymentPlan.service';
import { AllowedPermissions, TableData } from '@/types';
import { PaymentPlan } from '@/types/PaymentPlanTypes';

export const PaymentPlans = () => {
  const [t] = useTranslation('common');
  const [search, setSearch] = useState<string>();
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [selectedPaymentPlan, setSelectedPaymentPlan] = useState<PaymentPlan>();
  const navigate = useNavigate();

  const permissions: AllowedPermissions = {
    createPermission: PERMISSION_TYPE.CREATE_PAYMENT_PLAN,
    editPermission: PERMISSION_TYPE.UPDATE_PAYMENT_PLAN,
    deletePermission: PERMISSION_TYPE.DELETE_PAYMENT_PLAN,
    viewPermission: PERMISSION_TYPE.VIEW_PAYMENT_PLANS
  };

  const {
    isLoading,
    data: allPaymentPlans,
    isFetchingNextPage,
    refetch,
    fetchNextPage,
    hasNextPage
  } = useGetAllPaymentPlans(PAGE_SIZE, search, 'Id', false);

  const formatData = (): TableData[] => {
    return allPaymentPlans?.pages.flatMap((page) =>
      page.data?.items.map((paymentPlanData: PaymentPlan) => ({
        ...paymentPlanData,
        activeServices: paymentPlanData.organizationPermissions.length,
        enabled: true
      }))
    ) as TableData[];
  };

  const searchCallback = (query: string) => {
    setSearch(query);
  };

  const handleCreatePlan = () => {
    navigate(PRIVATE_PAGE_ROUTES.CREATE_PAYMENT_PLAN);
  };

  const handleEditPlan = (row: PaymentPlan) => {
    const editPlanUrl = PRIVATE_PAGE_ROUTES.EDIT_PAYMENT_PLAN.replace(':planId', row.id.toString());
    navigate(editPlanUrl);
  };

  const handleDeletePlan = (row: PaymentPlan) => {
    setIsOpenDeleteModal(true);
    setSelectedPaymentPlan(row);
  };

  const handleViewDetails = (row: PaymentPlan) => {
    const editPlanUrl = PRIVATE_PAGE_ROUTES.SINGLE_PAYMENT_PLAN.replace(
      ':planId',
      row.id.toString()
    );
    navigate(editPlanUrl);
  };

  const handleCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
    setSelectedPaymentPlan(undefined);
  };

  const { mutate: deletePaymentPlan } = useDeletePaymentPlanMutation(queryClient, {
    onSuccess: () => {
      setIsOpenDeleteModal(false);
      refetch();
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  return (
    <div className='flex size-full flex-col items-center'>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className='flex h-screen w-full flex-col px-3 pb-3'>
          {allPaymentPlans && (
            <Table
              data={formatData()}
              columns={getPaymentPlanColumns()}
              tableTitle={t('paymentPlans')}
              addButtonLabel={t('addPlan')}
              permissions={permissions}
              search={searchCallback}
              handleCreate={handleCreatePlan}
              handleEdit={(item) => handleEditPlan(item as PaymentPlan)}
              handleDelete={(item) => handleDeletePlan(item as PaymentPlan)}
              handleViewDetails={(item) => handleViewDetails(item as PaymentPlan)}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              hiddenColumnsMobile={[]}
              mobileColumnOffset={3}
              numberOfAppliedFilters={0}
            />
          )}
          {selectedPaymentPlan && (
            <ConfirmationModal
              isOpen={isOpenDeleteModal}
              message={t('deleteMessage', {
                item: `${selectedPaymentPlan.name}`
              })}
              onClose={handleCloseDeleteModal}
              onConfirm={() => {
                selectedPaymentPlan &&
                  selectedPaymentPlan.id &&
                  deletePaymentPlan(selectedPaymentPlan.id);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};
